import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MInputField } from "@/components/ui/atoms/MInputField";
import FormHeader from "@/components/ui/organisms/MHeader";

import { MButton } from "@/components/ui/atoms/MButtons";
import { IconCalendar } from "@tabler/icons-react";
import {
  useProfileUpdate,
  useMembers,
  getMemberInfo,
} from "@/hooks/useMembers";
import MGender from "@/components/ui/molecules/MGender";
import MDatePicker from "@/components/ui/molecules/MDatePicker";
import MToast from "@/components/ui/atoms/MToast";
import MRadio from "@/components/ui/atoms/MRadio";
import { isChild } from "@/types/member";

const UpdateProfile: React.FC = () => {
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    variant: "success" | "error";
  }>({ open: false, message: "", variant: "success" });
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const { handleProfileUpdate } = useProfileUpdate();
  const { getMember } = useMembers({ autoFetch: false });
  const [loading, setLoading] = useState(true);

  // UI state
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dobMonth, setDobMonth] = useState<number | undefined>(undefined);
  const [dobYear, setDobYear] = useState<number | undefined>(undefined);
  const [gender, setGender] = useState<number | undefined>(undefined);
  const [genderDrawerOpen, setGenderDrawerOpen] = useState(false);
  const [dobPickerOpen, setDobPickerOpen] = useState(false);
  const [petType, setPetType] = useState("");
  const [breed, setBreed] = useState("");
  const [saving, setSaving] = useState(false);
  const memberInfo = getMemberInfo();
  const [updateMember, setUpdateMember] = useState<any>(null);

  useEffect(() => {
    if (!memberId) return;
    setLoading(true);
    getMember(memberId)
      .then((member) => {
        setUpdateMember(member);
        setFirstName(member.first_name || "");
        setMiddleName(member.middle_name || "");
        setLastName(member.last_name || "");
        setDobMonth(member.dob_month);
        setDobYear(member.dob_year);
        setGender(
          typeof member.gender === "number" ? member.gender : undefined
        );
        setPetType(member.other_attrs?.petType || "");
        setBreed(member.other_attrs?.breed || "");
      })
      .finally(() => setLoading(false));
  }, [memberId]);

  // If the existing DOB makes this member an adult, lock the picker to 18+
  // so the profile cannot be downgraded to a child via DOB edit.
  const existingIsAdult =
    !!updateMember &&
    !!updateMember.dob_year &&
    !!updateMember.dob_month &&
    !isChild(updateMember);
  const adultMaxDate = existingIsAdult
    ? (() => {
        const today = new Date();
        return new Date(today.getFullYear() - 18, today.getMonth() + 1, 0);
      })()
    : undefined;

  const isChanged =
    updateMember &&
    (firstName !== (updateMember.first_name || "") ||
      middleName !== (updateMember.middle_name || "") ||
      lastName !== (updateMember.last_name || "") ||
      dobMonth !== updateMember.dob_month ||
      dobYear !== updateMember.dob_year ||
      gender !== updateMember.gender ||
      petType !== (updateMember.other_attrs?.petType || "") ||
      breed !== (updateMember.other_attrs?.breed || ""));

  async function handleUpdate() {
    const updateData = {
      first_name: firstName,
      middle_name: middleName,
      last_name: lastName,
      gender:
        typeof gender === "number"
          ? gender
          : gender
          ? Number(gender)
          : undefined,
      dob_day: undefined,
      dob_month: dobMonth,
      dob_year: dobYear,
      other_attrs: {
        petType,
        breed,
      },
    };
    setSaving(true);
    const result = await handleProfileUpdate({
      memberId: memberId || memberInfo.id,
      updateData,
      isChanged,
      onSavingChange: setSaving,
    });
    setSaving(false);
    if (result.success) {
      setToast({
        open: true,
        message: "Profile updated successfully",
        variant: "success",
      });
      setTimeout(() => navigate(-1), 1200);
    } else {
      setToast({
        open: true,
        message: result.error?.message || "Profile update failed",
        variant: "error",
      });
    }
  }

  return (
    <div className="profile-page-root">
      <FormHeader
        module="Profile"
        title="Edit Member Profile"
        memberInfo={memberInfo}
        onBack={() => navigate(-1)}
      />
      {loading || !memberInfo ? (
        <div className="flex items-center justify-center h-32">Loading...</div>
      ) : updateMember?.member_type === "p" ? (
        <div className="flex flex-col gap-6 flex-1">
          <MInputField
            placeholder="Enter Pet Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            variant="Underlined"
            size="md"
            showLabel
            label="Pet Name"
            mandatory
          />
          <MInputField
            label="DATE OF BIRTH"
            showLabel
            placeholder="Not Answered"
            variant="Underlined"
            size="md"
            value={
              dobMonth && dobYear
                ? (() => {
                    const date = new Date(
                      Number(dobYear),
                      Number(dobMonth) - 1
                    );
                    return date.toLocaleString("default", {
                      month: "short",
                      year: "numeric",
                    });
                  })()
                : ""
            }
            onClick={() => setDobPickerOpen(true)}
            readOnly
            icon={<IconCalendar />}
            mandatory
          />
          <MDatePicker
            open={dobPickerOpen}
            onClose={() => setDobPickerOpen(false)}
            onDone={(date) => {
              setDobMonth(date.getMonth() + 1);
              setDobYear(date.getFullYear());
              setDobPickerOpen(false);
            }}
            initialValue={
              dobMonth && dobYear ? new Date(dobYear, dobMonth - 1) : undefined
            }
            title="Select Month and Year of Birth"
            hideDate
          />
          <MInputField
            label="GENDER"
            showLabel
            placeholder="Not Answered"
            variant="Underlined"
            size="md"
            value={
              gender === 1
                ? "Male"
                : gender === 2
                ? "Female"
                : gender === 3
                ? "Other"
                : ""
            }
            onClick={() => setGenderDrawerOpen(true)}
            readOnly
            mandatory
          />
          <MGender
            open={genderDrawerOpen}
            onClose={() => setGenderDrawerOpen(false)}
            onDone={(g) => {
              let genderNum = undefined;
              if (g === "male") genderNum = 1;
              else if (g === "female") genderNum = 2;
              else if (g === "other") genderNum = 3;
              setGender(genderNum);
              setGenderDrawerOpen(false);
            }}
            initialValue={
              gender === 1
                ? "male"
                : gender === 2
                ? "female"
                : gender === 3
                ? "other"
                : ""
            }
          />
          <div className="flex flex-col gap-2">
            <div className="profile-input-label">
              Is your Pet a Cat or Dog?<span style={{ color: "red" }}>*</span>
            </div>
            <div className="flex gap-6">
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <MRadio
                  name="petType"
                  value="dog"
                  checked={petType === "dog"}
                  onChange={(e) => setPetType(e.target.value)}
                  required
                />
                Dog
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <MRadio
                  name="petType"
                  value="cat"
                  checked={petType === "cat"}
                  onChange={(e) => setPetType(e.target.value)}
                  required
                />
                Cat
              </label>
            </div>
          </div>
          <MInputField
            label="Breed"
            showLabel
            placeholder="Enter Breed"
            variant="Underlined"
            size="md"
            value={breed}
            onChange={(e) => setBreed(e.target.value)}
            mandatory
          />
        </div>
      ) : (
        <div className="flex flex-col gap-6 flex-1">
          <MInputField
            placeholder="Enter your First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            variant="Underlined"
            size="md"
            showLabel
            label="First Name"
            mandatory
          />
          <MInputField
            label="MIDDLE NAME"
            showLabel
            placeholder="Not Answered"
            variant="Underlined"
            size="md"
            value={middleName}
            onChange={(e) => setMiddleName(e.target.value)}
          />
          <MInputField
            label="LAST NAME"
            showLabel
            placeholder="Not Answered"
            variant="Underlined"
            size="md"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            mandatory
          />
          <MInputField
            label="TELL US ABOUT THEIR  MONTH AND YEAR OF BIRTH"
            showLabel
            placeholder="Not Answered"
            variant="Underlined"
            size="md"
            value={
              dobMonth && dobYear
                ? (() => {
                    const date = new Date(
                      Number(dobYear),
                      Number(dobMonth) - 1
                    );
                    return date.toLocaleString("default", {
                      month: "short",
                      year: "numeric",
                    });
                  })()
                : ""
            }
            onClick={() => setDobPickerOpen(true)}
            readOnly
            icon={<IconCalendar />}
            mandatory
          />
          <MDatePicker
            open={dobPickerOpen}
            onClose={() => setDobPickerOpen(false)}
            onDone={(date) => {
              setDobMonth(date.getMonth() + 1);
              setDobYear(date.getFullYear());
              setDobPickerOpen(false);
            }}
            initialValue={
              dobMonth && dobYear ? new Date(dobYear, dobMonth - 1) : undefined
            }
            maxDate={adultMaxDate}
            title="Select Month and Year of Birth"
            hideDate
          />
          <MInputField
            label="WHAT IS THEIR GENDER"
            showLabel
            placeholder="Not Answered"
            variant="Underlined"
            size="md"
            value={
              gender === 1
                ? "Male"
                : gender === 2
                ? "Female"
                : gender === 3
                ? "Other"
                : ""
            }
            onClick={() => setGenderDrawerOpen(true)}
            readOnly
            mandatory
          />
          <MGender
            open={genderDrawerOpen}
            onClose={() => setGenderDrawerOpen(false)}
            onDone={(g) => {
              let genderNum = undefined;
              if (g === "male") genderNum = 1;
              else if (g === "female") genderNum = 2;
              else if (g === "other") genderNum = 3;
              setGender(genderNum);
              setGenderDrawerOpen(false);
            }}
            initialValue={
              gender === 1
                ? "male"
                : gender === 2
                ? "female"
                : gender === 3
                ? "other"
                : ""
            }
          />
        </div>
      )}
      <div className="profile-done-btn-row">
        <MButton
          className="profile-done-btn"
          disabled={!isChanged || saving}
          onClick={handleUpdate}
        >
          {saving ? "Saving..." : "Done"}
        </MButton>
      </div>
      {toast.open && (
        <MToast
          message={toast.message}
          variant={toast.variant}
          onClose={() => setToast({ ...toast, open: false })}
        />
      )}
    </div>
  );
};

export default UpdateProfile;
